import * as React from 'react'
import {Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, TextField} from '@mui/material'
import {useSnackbar} from 'notistack'
import {primaryBtnSx, secondaryBtnSx} from '../../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../../styles/foundation/typography'
import {SECONDARY_BLUE} from '../../../styles/foundation/colors'
import {nowDate} from './data/clock'
import {missingForDispute, missingSentence} from './state/evidenceIntake'

/**
 * ── Pure transition logic ──────────────────────────────────────────────
 * Everything in this section is a plain function: no React, no dialogs, no
 * toasts, no I/O. That is deliberate — it is what actions.test.js exercises,
 * so the state-machine rules can be verified without mounting a component or
 * driving a confirm dialog.
 */

export const ACTION_LABELS = {
  dispute: 'Dispute',
  accept: 'Accept',
  undoAccept: 'Undo',
  freezeSku: 'Freeze SKU',
}

/**
 * Which actions the footer should offer for a given status.
 * - NEW: the merchant hasn't acted yet — offer Dispute or Accept.
 * - ACCEPTED_BY_MERCHANT / DISPUTED_BY_MERCHANT: the *merchant's own* most
 *   recent action, not yet countersigned by the courier or Prozo — still
 *   reversible, so offer Undo.
 * - Anything else (DISPUTED_BY_COURIER, ACCEPTED_BY_COURIER,
 *   ACCEPTED_PROSHIP, REJECTED_PROSHIP, AUTO_ACCEPTED) involves a
 *   counterparty decision or is a terminal outcome — no Dispute/Accept/Undo.
 * - Freeze SKU is unconditional: it doesn't touch this dispute's status at
 *   all (see applyTransition's 'freezeSku' case), so it is always offered.
 */
export function availableActions(status) {
  const actions = []
  if (status === 'NEW') {
    actions.push('dispute', 'accept')
  } else if (status === 'ACCEPTED_BY_MERCHANT' || status === 'DISPUTED_BY_MERCHANT') {
    actions.push('undoAccept')
  }
  actions.push('freezeSku')
  return actions
}

/**
 * The subset of `rows` for which accepting is a valid transition, decided by
 * availableActions() above rather than by a second copy of the rule — a bulk
 * action that disagreed with the per-row buttons about what can be accepted
 * would be worse than no bulk action at all.
 *
 * Pure, and exported, because the selection bar's count ("Accept charge on N
 * of M selected") and the set the confirm dialog acts on must be the same
 * answer, and that has to be assertable without mounting the grid.
 */
export function acceptableRows(rows) {
  return (rows || []).filter((r) => r && availableActions(r.status).includes('accept'))
}

/**
 * Why this row cannot be disputed yet, as one sentence, or '' when it can.
 *
 * Deliberately NOT folded into availableActions(): a Dispute button that
 * vanished on an unevidenced row would leave the merchant with nothing to
 * click and nothing to read. The action stays offered, and the surfaces that
 * offer it render it disabled beside this reason (DisputeDrawer.js).
 *
 * The rule itself is state/evidenceIntake.js's missingForDispute — the same
 * function the drawer's evidence form gates its own Submit on.
 */
export function disputeBlockedReason(row) {
  return missingSentence(missingForDispute(row))
}

/**
 * Computes the next row for a given action. Returns a brand-new row object
 * (never mutates `row`) with `statusLog` appended whenever the status
 * actually changes.
 *
 * MUTATIONS ARE LOCAL-ONLY: this function does not call an API and nothing
 * it returns is persisted anywhere. `DISPUTES` (data/noberoDisputes.js) is a
 * frozen module export; index.js copies it into React state once and layers
 * every action's result on top of that state. A page reload re-imports the
 * frozen array and discards every change made here. Do not mistake this for
 * a working write path to a backend.
 */
export function applyTransition(row, actionType) {
  if (!row) throw new Error('applyTransition requires a row')
  const at = nowDate().toISOString()

  const withStatus = (status) => ({
    ...row,
    status,
    updatedAt: at,
    statusLog: [...(row.statusLog || []), {status, at, by: 'Merchant'}],
  })

  switch (actionType) {
    case 'dispute': {
      if (row.status !== 'NEW') throw new Error(`Cannot dispute a ${row.status} row`)
      // THE gate, not a UI nicety. Every route into a dispute — the drawer
      // footer today, whatever a per-row or bulk control becomes tomorrow —
      // lands here, so refusing here is the only way the rule cannot be
      // reached around. The disabled buttons upstream exist so a merchant
      // never has to discover the refusal by hitting it.
      const blocked = disputeBlockedReason(row)
      if (blocked) throw new Error(`AWB ${row.awb} cannot be disputed yet. ${blocked}`)
      return withStatus('DISPUTED_BY_MERCHANT')
    }

    case 'accept':
      if (row.status !== 'NEW') throw new Error(`Cannot accept a ${row.status} row`)
      return withStatus('ACCEPTED_BY_MERCHANT')

    case 'undoAccept':
      if (row.status !== 'ACCEPTED_BY_MERCHANT' && row.status !== 'DISPUTED_BY_MERCHANT') {
        throw new Error(`Cannot undo a ${row.status} row`)
      }
      return withStatus('NEW')

    case 'freezeSku':
      // Freezing a SKU flags it in the separate Weight Freeze module; it is
      // not a dispute-status change, so the row (and its statusLog) is
      // returned untouched. No-op transition, on purpose.
      return row

    default:
      throw new Error(`Unknown action: ${actionType}`)
  }
}

/**
 * ── Side effects: confirm dialog + toast ───────────────────────────────
 * Thin layer around applyTransition() above. Not covered by unit tests per
 * the task's testing policy (presentational rendering is out of scope) —
 * the transition logic it wraps is what's actually tested.
 */

// Every entry takes the same `{row, rows}` context so one dialog can serve
// both a single row and a whole selection. `rows` is populated for the bulk
// entry only; `row` for the four single-row ones.
const CONFIRM_COPY = {
  dispute: {
    title: () => 'Raise this dispute?',
    // No longer "make sure your evidence is attached first": the button that
    // opens this dialog cannot be reached without it (see applyTransition's
    // 'dispute' case), so the old line asked for something already done.
    body: ({row}) => `AWB ${row.awb} moves to "Disputed By Merchant" and the courier is notified, with the figures and evidence on file for this shipment.`,
    confirmLabel: 'Dispute',
    success: ({row}) => `Dispute raised for AWB ${row.awb}`,
  },
  accept: {
    title: () => 'Accept this charge?',
    body: ({row}) => `This confirms the courier's applied weight for AWB ${row.awb} and moves it to Resolved.`,
    confirmLabel: 'Accept',
    success: ({row}) => `AWB ${row.awb} marked as accepted`,
  },
  // The bulk twin of `accept`, deliberately the same dialog rather than a
  // second confirmation of its own: same title shape, same wording for what
  // acceptance means, same Accept button. It states the count where the
  // single-row copy states the AWB, and then stops exactly where that one
  // stops. It must not claim the transition is final: an accepted row still
  // reports 'undoAccept' from availableActions(), and the drawer renders that
  // button, so any finality wording here would be contradicted by the state
  // machine and by the Undo the merchant can see on the row.
  acceptMany: {
    title: ({rows}) => `Accept ${rows.length} ${rows.length === 1 ? 'charge' : 'charges'}?`,
    body: ({rows}) => `This confirms the courier's applied weight on ${rows.length} ${rows.length === 1 ? 'shipment' : 'shipments'} and moves ${rows.length === 1 ? 'it' : 'them'} to Resolved.`,
    confirmLabel: 'Accept',
    success: ({rows}) => `${rows.length} ${rows.length === 1 ? 'discrepancy' : 'discrepancies'} accepted`,
  },
  undoAccept: {
    title: () => 'Undo this action?',
    body: ({row}) => `This returns AWB ${row.awb} to "New" so you can decide again.`,
    confirmLabel: 'Undo',
    success: ({row}) => `AWB ${row.awb} reverted to New`,
  },
  freezeSku: {
    title: () => 'Freeze this SKU?',
    body: ({row}) => `Weight freezes are raised in Merchant Weight Freeze. This takes you there to raise one${row.sku ? ` for ${row.sku}` : ''}. It does not change this dispute.`,
    confirmLabel: 'Go to Merchant Weight Freeze',
    success: ({row}) => (row.sku ? `Raise the freeze for ${row.sku} in Merchant Weight Freeze` : 'Raise the freeze in Merchant Weight Freeze'),
  },
}

/** The actions a Prozo login takes on the merchant's behalf — each needs a remark (WdActionAccessRules). */
const ON_BEHALF_ACTIONS = ['dispute', 'accept', 'acceptMany']

/**
 * `onChange(updatedRow)` is how the caller receives the mutated row — see
 * applyTransition's doc comment: with no `execute`, nothing here talks to a
 * server, it only computes the next row and hands it back for the caller's
 * own React state.
 *
 * `execute(actionType, rowOrRows, {remark})` — optional — is the live-backend seam:
 * when provided, the confirm dialog's Confirm awaits it INSTEAD of running
 * applyTransition locally. It must resolve to the updated row (array of rows
 * for 'acceptMany'), or throw an Error whose message is fit to toast. The
 * demo harness and the unit tests never pass it, so the local sync path below
 * is exactly what it always was.
 *
 * `onBehalf` marks a Prozo login, which disputes and accepts ON THE MERCHANT'S BEHALF: the server
 * refuses either without a reason on record, so the dispute, accept and bulk-accept dialogs all
 * require a remark. Without it only the dispute dialog offers one, and it is optional.
 *
 * Returns `{accept, dispute, undoAccept, freezeSku, acceptMany,
 * confirmDialog}`. Each of the four single-row action functions takes a row
 * and returns a Promise that resolves to the updated row, or `null` if the
 * user cancelled the confirm dialog; `acceptMany` takes an array and resolves
 * to the array of rows that were actually accepted.
 * `confirmDialog` is the dialog element itself — render it once, anywhere,
 * alongside whatever calls these functions (see index.js).
 */
export function useDisputeActions({onChange, execute, onBehalf = false} = {}) {
  const {enqueueSnackbar} = useSnackbar()
  const [pending, setPending] = React.useState(null) // {actionType, row, resolve}
  // The dialog's remark. A merchant may add one to a dispute; a Prozo login acting on the merchant's
  // behalf must give one to dispute or accept (WdActionAccessRules). A missing remark is reported in
  // place and the dialog stays open, so nothing already entered is lost.
  const [remark, setRemark] = React.useState('')
  const [remarkError, setRemarkError] = React.useState('')
  const remarkRequired = (actionType) => onBehalf && ON_BEHALF_ACTIONS.includes(actionType)
  const remarkShown = (actionType) => actionType === 'dispute' || remarkRequired(actionType)
  const [displayed, setDisplayed] = React.useState(null) // last non-null `pending`, kept during the close transition

  React.useEffect(() => {
    if (pending) setDisplayed(pending)
  }, [pending])

  const requestAction = React.useCallback((actionType, row) => new Promise((resolve) => {
    setRemark('')
    setRemarkError('')
    setPending({actionType, row, resolve})
  }), [])

  const dispute = React.useCallback((row) => requestAction('dispute', row), [requestAction])
  const accept = React.useCallback((row) => requestAction('accept', row), [requestAction])
  const undoAccept = React.useCallback((row) => requestAction('undoAccept', row), [requestAction])
  const freezeSku = React.useCallback((row) => requestAction('freezeSku', row), [requestAction])

  /**
   * Accept a whole selection. Takes the rows the caller has ALREADY narrowed
   * with acceptableRows() — this is the same confirm-then-apply path the
   * single-row accept uses, just looped, so one row and fifty cannot diverge.
   * Resolves to the array of updated rows, or `null` if the merchant
   * cancelled.
   */
  const acceptMany = React.useCallback((rows) => new Promise((resolve) => {
    setPending({actionType: 'acceptMany', rows: rows || [], resolve})
  }), [])

  const handleCancel = () => {
    if (pending) pending.resolve(null)
    setPending(null)
  }

  const handleConfirm = () => {
    if (!pending) return
    const {actionType, row, resolve} = pending
    if (remarkRequired(actionType) && !remark.trim()) {
      setRemarkError('Please enter a remark.')
      return
    }

    // Live-backend path: the server owns the transition. The dialog closes
    // immediately (the action is in flight, not cancellable) and the toast
    // reports what the server actually did — success with the row it
    // returned, or the server's own refusal sentence as an error.
    if (execute) {
      const subject = actionType === 'acceptMany' ? (pending.rows || []) : row
      Promise.resolve()
        .then(() => execute(actionType, subject, {remark: remark.trim()}))
        .then((result) => {
          if (actionType === 'acceptMany') {
            const updatedRows = result || []
            updatedRows.forEach((r) => { if (onChange && r) onChange(r) })
            enqueueSnackbar(CONFIRM_COPY.acceptMany.success({rows: updatedRows}), {
              variant: updatedRows.length ? 'success' : 'warning',
            })
            resolve(updatedRows)
            return
          }
          if (onChange && result) onChange(result)
          enqueueSnackbar(CONFIRM_COPY[actionType].success({row: result || row}), {variant: 'success'})
          resolve(result || null)
        })
        .catch((err) => {
          enqueueSnackbar((err && err.message) || 'Action failed', {variant: 'error'})
          resolve(null)
        })
      setPending(null)
      return
    }

    // The bulk branch runs the SAME applyTransition per row. onChange is
    // called once per row rather than once with a batch: index.js's handler
    // is a functional setState, so N sequential calls compose, and a row that
    // somehow refused the transition is skipped instead of aborting the rest.
    // The toast then reports what actually landed, not what was asked for.
    if (actionType === 'acceptMany') {
      const updatedRows = []
      ;(pending.rows || []).forEach((r) => {
        let next
        try {
          next = applyTransition(r, 'accept')
        } catch {
          return
        }
        updatedRows.push(next)
        if (onChange) onChange(next)
      })
      enqueueSnackbar(CONFIRM_COPY.acceptMany.success({rows: updatedRows}), {
        variant: updatedRows.length ? 'success' : 'warning',
      })
      setPending(null)
      resolve(updatedRows)
      return
    }

    let updated
    try {
      updated = applyTransition(row, actionType)
    } catch (err) {
      enqueueSnackbar(err.message, {variant: 'error'})
      setPending(null)
      resolve(null)
      return
    }
    if (onChange) onChange(updated)
    enqueueSnackbar(CONFIRM_COPY[actionType].success({row: updated}), {variant: 'success'})
    setPending(null)
    resolve(updated)
  }

  const copy = displayed ? CONFIRM_COPY[displayed.actionType] : null

  // maxWidth 'xs' with a 380px cap: the default 'sm' gave a ~600px box to two
  // short lines of text, and because the dialog is portaled to document.body it
  // sits outside the page's own scale, so it read as the largest thing on
  // screen. The type is brought onto the module's scale for the same reason —
  // DialogContentText defaults to body1 (1rem), which was the only 16px text
  // anywhere in the module and overrode the app's own MuiDialogContent size.
  // The buttons drop MUI's uppercase 14px for the module's own button styles,
  // so they match every other Accept / Dispute pair the merchant has clicked.
  const confirmDialog = (
    <Dialog open={!!pending} onClose={handleCancel} maxWidth='xs' fullWidth PaperProps={{sx: {maxWidth: 380}}}>
      {copy && displayed && (
        <>
          <DialogTitle sx={{fontFamily: FONT, fontSize: SIZE.base, fontWeight: WEIGHT.semibold, pb: 1}}>
            {copy.title(displayed)}
          </DialogTitle>
          <DialogContent sx={{pb: 1}}>
            <DialogContentText sx={{fontFamily: FONT, fontSize: SIZE.xs, lineHeight: 1.5}}>
              {copy.body(displayed)}
            </DialogContentText>
            {remarkShown(displayed.actionType) && (
              <TextField
                fullWidth multiline minRows={2} size='small'
                autoFocus={remarkRequired(displayed.actionType)}
                label={remarkRequired(displayed.actionType) ? 'Remark (required)' : 'Remark (optional)'}
                value={remark}
                onChange={(e) => { setRemark(e.target.value); if (remarkError) setRemarkError('') }}
                error={!!remarkError}
                helperText={remarkError || (!remarkRequired(displayed.actionType)
                  ? 'Recorded on the dispute with your evidence.'
                  : displayed.actionType === 'dispute'
                    ? "You are signed in as Prozo, so this dispute is filed on the merchant's behalf and needs a reason."
                    : "You are signed in as Prozo, so this charge is accepted on the merchant's behalf and needs a reason.")}
                inputProps={{maxLength: 500}}
                InputProps={{sx: {fontFamily: FONT, fontSize: SIZE.xs}}}
                InputLabelProps={{sx: {fontFamily: FONT, fontSize: SIZE.xs}}}
                FormHelperTextProps={{sx: {fontFamily: FONT, fontSize: SIZE['2xs'], mx: 0}}}
                sx={{mt: 1.5}}
              />
            )}
          </DialogContent>
          <DialogActions sx={{px: 3, pb: 2, pt: 0}}>
            <Button onClick={handleCancel} sx={{...secondaryBtnSx, fontSize: SIZE['2xs']}}>Cancel</Button>
            <Button onClick={handleConfirm} autoFocus={!remarkRequired(displayed.actionType)} sx={{...primaryBtnSx, bgcolor: SECONDARY_BLUE, fontSize: SIZE['2xs'], '&:hover': {bgcolor: SECONDARY_BLUE, filter: 'brightness(0.92)'}}}>
              {copy.confirmLabel}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  )

  return {dispute, accept, undoAccept, freezeSku, acceptMany, confirmDialog}
}
